import { Channel } from '@src/api/shared/community/channelApiClient';
import { Chat, PinnedChat as PinnedChatType } from '@src/api/shared/community/chatApiClient';
import { ChatReaction } from '@src/api/shared/community/chatReactionApiClient';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useReactionContext } from '@src/context/ReactionContext';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import ChatActionMenuPc from '@src/features/shared/community/components/chat/ChatActionMenuPc';
import ChatActionMenuSp from '@src/features/shared/community/components/chat/ChatActionMenuSp';
import ChatItemHeader from '@src/features/shared/community/components/chat/chatList/ChatItemHeader';
import ReactionItem from '@src/features/shared/community/components/chat/ReactionItem';
import Tooltip from '@src/features/shared/community/components/chat/Tooltip';
import { ChatBoxHandle } from '@src/features/shared/community/components/ChatBox';
import { generateHtml } from '@src/utils/chat';
import { handleMentionClick, transformMentionsInHtml } from '@src/utils/mentionUtils';
import React, { useRef, useState } from 'react';

interface PinnedChatProps {
  pinnedChat: PinnedChatType;
  channel: Channel;
  isLoggedIn: boolean;
  chatBoxRef: React.RefObject<ChatBoxHandle>;
  onPinClick: (chatId: number | undefined) => Promise<void>;
  onPinnedChatClick: () => void;
  displayTooltip: (duration: number) => void;
  showTooltip: boolean;
  mentionClickedChatId: number | null;
  setMentionClickedChatId: React.Dispatch<React.SetStateAction<number | null>>;
}

const PRESS_LONG_TIME = 600;

const PinnedChat: React.FC<PinnedChatProps> = ({
  pinnedChat,
  channel,
  isLoggedIn,
  chatBoxRef,
  onPinClick,
  onPinnedChatClick,
  displayTooltip,
  showTooltip,
  mentionClickedChatId,
  setMentionClickedChatId,
}) => {
  const [isPinnedChatMenuOpen, setIsPinnedChatMenuOpen] = useState<boolean>(false);
  const { reactions: reactionState } = useReactionContext();
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  let touchTimer: NodeJS.Timeout;
  const { profile } = useSharedAuth();
  const currentUserType = profile?.isTenant
    ? ChannelMemberUserTypeEnum.TENANT_USER
    : ChannelMemberUserTypeEnum.COMPANY_USER;
  const currentUserId = profile?.isTenant
    ? profile?.tenantProfile?.user?.id
    : profile?.companyProfile?.user?.id;

  // 固定チャットのリアクション
  const pinnedChatReactions =
    (pinnedChat as PinnedChatType & { reactions?: ChatReaction[] })?.reactions || [];

  const handlePinnedChatLongPress = () => {
    setIsPinnedChatMenuOpen(true);
  };

  const handlePinnedChatMenuClose = () => {
    setIsPinnedChatMenuOpen(false);
  };

  return (
    <div className='sticky top-[60px] z-30 bg-white lg:top-[61px]'>
      <div
        className='group relative flex select-none flex-col gap-1 bg-[#FCFBE8] px-6 py-3 transition duration-300'
        onTouchStart={(e) => {
          if (e.touches.length === 1) {
            touchTimer = setTimeout(() => {
              handlePinnedChatLongPress();
            }, PRESS_LONG_TIME);
          }
        }}
        onTouchEnd={() => {
          clearTimeout(touchTimer);
        }}
        onTouchCancel={() => {
          clearTimeout(touchTimer);
        }}
        onClick={onPinnedChatClick}
      >
        <ChatItemHeader
          channelCode={channel.code}
          chat={pinnedChat}
          channelVisibility={channel.visibility}
          pinnedChat={pinnedChat}
          isLoggedIn={isLoggedIn}
        />

        <div
          className='tiptap-content line-clamp-2 min-w-0 break-all pl-12 text-sm text-[#000] md:pl-0'
          dangerouslySetInnerHTML={{
            __html: transformMentionsInHtml(
              generateHtml(pinnedChat.message, pinnedChat.is_edited),
              isLoggedIn,
              currentUserId,
              currentUserType,
              pinnedChat.id,
            ),
          }}
          onClick={(e) =>
            handleMentionClick(e, pinnedChat.id, setMentionClickedChatId, displayTooltip)
          }
        />

        <div className='no-scrollbar mt-2 flex flex-nowrap gap-1 overflow-x-auto pb-1 pl-12 md:pl-0'>
          {(reactionState[pinnedChat.id] || pinnedChatReactions)?.map((reaction, index) => (
            <ReactionItem
              key={`${reaction.emoji}-${index}`}
              chatId={pinnedChat.id}
              reaction={reaction}
              allReactions={reactionState[pinnedChat.id] || pinnedChatReactions}
              blockParentClick={true}
            />
          ))}
        </div>

        <div
          className='absolute right-6 top-1 hidden group-hover:block lg:!hidden'
          onClick={(e) => e.stopPropagation()}
        >
          <ChatActionMenuPc
            chatId={pinnedChat.id}
            handlePinClick={() => onPinClick(undefined)}
            handleChatEditClick={() => chatBoxRef.current?.editPinnedChat(pinnedChat)}
            handleChatDeleteClick={() => chatBoxRef.current?.deletePinnedChat(pinnedChat)}
            handleChatReportClick={() => chatBoxRef.current?.reportPinnedChat?.(pinnedChat)}
            isPinned={true}
            chatMenuPermission={chatBoxRef.current?.getChatMenuPermission?.(pinnedChat as Chat)}
            currentReactions={reactionState[pinnedChat.id] || pinnedChatReactions}
          />
        </div>
      </div>

      {isPinnedChatMenuOpen && (
        <ChatActionMenuSp
          chatId={pinnedChat.id}
          handleCloseClick={handlePinnedChatMenuClose}
          handleChatLinkCopyClick={() => console.log('link copied')}
          handlePinClick={() => onPinClick(undefined)}
          handleChatEditClick={() => chatBoxRef.current?.editPinnedChat(pinnedChat)}
          handleChatDeleteClick={() => chatBoxRef.current?.deletePinnedChat(pinnedChat)}
          handleChatReportClick={() => chatBoxRef.current?.reportPinnedChat?.(pinnedChat)}
          isPinned={true}
          chatMenuPermission={chatBoxRef.current?.getChatMenuPermission?.(pinnedChat as Chat)}
          isLoggedIn={isLoggedIn}
          currentReactions={reactionState[pinnedChat.id] || pinnedChatReactions}
        />
      )}

      {showTooltip && mentionClickedChatId === pinnedChat.id && (
        <div
          ref={tooltipRef}
          className='absolute left-1/2 top-1/2 z-[999] -translate-x-1/2 -translate-y-1/2 select-none text-[12px] lg:left-[40%] lg:ml-[17%] lg:w-[290px]'
        >
          <Tooltip
            label='表示するには、ログインしてください。'
            weight='500'
          />
        </div>
      )}
    </div>
  );
};

export default PinnedChat;
